/** The source path of a reviewed imaging-study checkbox. */
export const IMAGING_STUDY_SOURCE_PATH = "structured_order.imaging_study";
/** A body site compared as words: case, width and separators never split a match. */
export function imagingBodySite(value) {
    return value
        .normalize("NFKC")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, " ");
}
/**
 * An ordered study's key on a prepared form set: its code, modality, body site, side and
 * contrast. A study no field of the forms shows is reported under this key, and the clinician's
 * confirmation that they wrote it in by hand names it the same way.
 */
export function imagingStudyIdentity(study) {
    return `IMAGING|${JSON.stringify([study.codeSystem, study.code, study.modality, imagingBodySite(study.bodySite), study.laterality, study.contrast])}`;
}
/**
 * A study name compared without its side qualifier ("Left knee X-ray" is "Knee X-ray" for the
 * left side), unless the body site itself names a side, and with equivalent spellings of the
 * modality folded together.
 */
export function imagingStudyName(value, bodySite, laterality) {
    let name = value.normalize("NFKC").trim().toLowerCase();
    if (["left", "right", "bilateral"].includes(laterality) &&
        !/\b(?:left|right|bilateral)\b/i.test(bodySite)) {
        const letter = laterality === "left" ? "l" : laterality === "right" ? "r" : "b";
        name = name
            .replace(new RegExp(`^${laterality}\\s+`), "")
            .replace(new RegExp(`\\s+(?:[-–—]\\s*)?${laterality}$`), "")
            .replace(new RegExp(`\\s*\\((?:${laterality}|${letter})\\)$`), "")
            .replace(new RegExp(`\\s+[-–—]\\s*${letter}$`), "");
    }
    return name
        .replace(/\bx[ -]?rays?\b|\bradiographs?\b/g, "radiograph")
        .replace(/\bus\b/g, "ultrasound")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}
/**
 * Whether a reviewed form checkbox stands for this ordered study. All dimensions must belong to
 * one reviewed study; separate orders cannot supply pieces.
 *
 * The renderer ticks a box only on this match, and the encounter shows a box ticked on the same
 * match, so the two can never disagree. `ignoreLaterality` is for choosing which form carries a
 * study: a study whose side is not chosen yet still belongs on the form that has its boxes.
 */
export function imagingStudyMatchesBinding(binding, study, options = {}) {
    const ignoreLaterality = options.ignoreLaterality === true;
    if (binding.origin !== "human_confirmed")
        return false;
    if (!ignoreLaterality && study.laterality === "unknown")
        return false;
    if (study.modality === "other" || binding.modality !== study.modality)
        return false;
    const bodySite = imagingBodySite(study.bodySite);
    // Some reviewed DXA choices name the exam/interval without an anatomical region.
    // Their exact study name or code still has to match below; other modalities need a site.
    if (!bodySite ||
        bodySite === "unknown" ||
        (bodySite === "unspecified" && study.modality !== "DXA"))
        return false;
    if (imagingBodySite(binding.bodySite) !== bodySite)
        return false;
    if (!ignoreLaterality && binding.laterality !== "any" && binding.laterality !== study.laterality)
        return false;
    if (binding.contrast === "unknown" ||
        (binding.contrast !== undefined && binding.contrast !== study.contrast))
        return false;
    if (binding.code !== undefined && binding.code.trim() !== study.code.trim())
        return false;
    if (binding.codeSystem !== undefined && binding.codeSystem !== study.codeSystem)
        return false;
    if (binding.code === undefined &&
        imagingStudyName(binding.studyName, binding.bodySite, binding.laterality) !==
            imagingStudyName(study.display, study.bodySite, study.laterality))
        return false;
    return true;
}
/**
 * The reviewed imaging-study boxes of a form: human-confirmed, automatically filled checkboxes
 * (or radios) of `structured_order.imaging_study`. These are the boxes the renderer can tick, so
 * a form carries a study exactly when one of them matches it.
 */
export function reviewedImagingStudyBindings(fields) {
    return fields.flatMap((field) => field.imagingStudyBinding !== undefined &&
        field.imagingStudyBinding.origin === "human_confirmed" &&
        field.reviewStatus === "reviewed" &&
        (field.fieldType === "checkbox" || field.fieldType === "radio") &&
        field.source.kind === "structured_order" &&
        field.source.path === IMAGING_STUDY_SOURCE_PATH &&
        field.disposition !== "manual" &&
        field.disposition !== "unused"
        ? [field.imagingStudyBinding]
        : []);
}
/** Whether any of a form's reviewed boxes carries this study, whatever side it is ordered on. */
export function formCarriesImagingStudy(bindings, study) {
    return bindings.some((binding) => imagingStudyMatchesBinding(binding, study, { ignoreLaterality: true }));
}
/**
 * The contrast a study is ordered with when nothing says otherwise. Plain radiography,
 * ultrasound, mammography, bone density, fluoroscopy and nuclear medicine studies are ordered
 * without an intravenous contrast choice; CT and MR need the clinician to say.
 */
export function defaultImagingContrast(modality) {
    return ["XR", "US", "MG", "DXA", "FL", "NM"].includes(modality) ? "none" : "unknown";
}
/**
 * A stable identity for one study and side: the modality, body site and study name, compared as
 * the renderer compares them. Codes are left out on purpose, because the same reviewed study can
 * be ordered by its catalogue code or by its reviewed name.
 */
export function imagingStudyKey(study) {
    return `IMAGING|${JSON.stringify([
        study.modality,
        imagingBodySite(study.bodySite),
        imagingStudyName(study.display, study.bodySite, study.laterality),
        study.laterality,
    ])}`;
}
/** The longest code an imaging order carries: a free-text study keeps its name within it. */
const IMAGING_CODE_MAX_LENGTH = 80;
/**
 * Resolves a form field to the ONE imaging study its checkbox orders, or undefined when the box
 * is not an unambiguous order input.
 *
 * Like a laboratory test box, a tick on such a box changes the ORDER; it never edits the PDF.
 * The requisition is re-rendered from the amended order, so what the paper says always comes
 * from the order. Only a reviewed, human-confirmed checkbox with an exact side qualifies: a
 * parent box whose sides are separate boxes ("any") is filled from the order, and a radio's
 * siblings would untick without an event, so neither is clickable.
 *
 * Shared by the renderer's form choice and the encounter viewer so the two cannot disagree.
 */
export function orderEditableImagingStudyForBinding(field) {
    const binding = field.imagingStudyBinding;
    if (binding === undefined ||
        field.reviewStatus !== "reviewed" ||
        field.fieldType !== "checkbox" ||
        field.source.kind !== "structured_order" ||
        field.source.path !== IMAGING_STUDY_SOURCE_PATH ||
        field.disposition === "manual" ||
        field.disposition === "unused" ||
        binding.origin !== "human_confirmed" ||
        binding.modality === "other" ||
        binding.laterality === "any" ||
        binding.laterality === "unknown")
        return undefined;
    const display = imagingStudyDisplay(binding);
    const coded = binding.code !== undefined && binding.codeSystem !== undefined;
    const study = {
        code: coded ? binding.code : display.slice(0, IMAGING_CODE_MAX_LENGTH),
        codeSystem: coded ? binding.codeSystem : "free_text",
        display,
        modality: binding.modality,
        bodySite: binding.bodySite,
        laterality: binding.laterality,
        contrast: binding.contrast ?? defaultImagingContrast(binding.modality),
    };
    // A box that states its contrast is its own choice: the same study with the other contrast
    // is another box, and ticking one must never read as ticking both.
    const key = binding.contrast === undefined
        ? imagingStudyKey(study)
        : `${imagingStudyKey(study)}|${binding.contrast}`;
    return { key, binding, study };
}
/** True when a clinician may tick this box to add or remove its imaging study. */
export function isOrderEditableImagingStudyField(field) {
    return orderEditableImagingStudyForBinding(field) !== undefined;
}
/**
 * The study name a box orders, without its side: "Left knee X-ray" orders "Knee X-ray" on the
 * left. The reviewed name is kept as written when the side is part of the anatomy.
 */
function imagingStudyDisplay(binding) {
    const name = binding.studyName.normalize("NFKC").trim();
    if (!["left", "right", "bilateral"].includes(binding.laterality) ||
        /\b(?:left|right|bilateral)\b/i.test(binding.bodySite))
        return name;
    const side = binding.laterality;
    const letter = side === "left" ? "L" : side === "right" ? "R" : "B";
    const stripped = name
        .replace(new RegExp(`^${side}\\s+`, "i"), "")
        .replace(new RegExp(`\\s+(?:[-–—]\\s*)?${side}$`, "i"), "")
        .replace(new RegExp(`\\s*\\((?:${side}|${letter})\\)$`, "i"), "")
        .replace(new RegExp(`\\s+[-–—]\\s*${letter}$`, "i"), "")
        .trim();
    if (stripped.length === 0)
        return name;
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}
//# sourceMappingURL=imaging-study-matching.js.map